/**
 * @file SessionAnnotationsContainer.js
 * @description handles rendering of session annotations.
 *              shared by: 
 *  
 *                  src/content/Document/Search/SearchSessionAnnotations/PublicationSessionAnnotations
 *                  src/content/Document/Search/SearchSessionAnnotations/SupplementSessionAnnotations
 *                  src/content/Document/Search/SearchSessionAnnotations/TableSessionAnnotations
 *
 * @copyright veriome labs, llc.  - all rights reserved.
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.md', which is part of this source code package.
 *
 * @author don michael
 * @since Feb 13, 2023 16:34
 */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { arrayExists, objectExists, objectHas } from 'src/@common/utilities';
import SessionAnnotationsForm from './SessionAnnotationsForm';

import {
    sessionAnnotationsDeleteRequest,
    sessionAnnotationsPageQueryRequest,
    sessionAnnotationsRowCheckAllSet,
    sessionAnnotationsRowCheckSet,
} from 'src/store/actions/sessionAnnotations/sessionAnnotationsDb.actions';

function SessionAnnotationsContainer() {
    const dispatch = useDispatch();

    // start: redux state --------------------------
    const sessionAnnotationsDbState = useSelector((state) => {
        return state.sessionAnnotationsDb;
    });

    const {
        metadata,
        searchResult,
        annotatorId,
        docId,
        docType,
    } = sessionAnnotationsDbState;
    // end: redux state -----------------------------

    const [annotations, setAnnotations] = useState([]);
    const [pageCount, setPageCount] = useState(0);
    const [pageNo, setPageNo] = useState(0);
    const [pageTitle, setPageTitle] = useState('');
    const [queryCount, setQueryCount] = useState(0);

    useEffect(() => {

        if (objectExists(searchResult)) {

            if (objectHas(searchResult, 'pageDocs') &&
                arrayExists(searchResult.pageDocs)) {
                setAnnotations(searchResult.pageDocs);
            } else {
                setAnnotations([]);
            }

            if (objectHas(searchResult, 'queryCount')) {
                setQueryCount(searchResult.queryCount);

                if (searchResult.queryCount === 1) {
                    setPageTitle('One Annotation');
                } else {
                    setPageTitle(`${searchResult.queryCount} Annotations`);
                }

            } else {
                setPageTitle('No Annotations');
                setQueryCount(0);
            }

            if (objectHas(searchResult, 'pageCount')) {
                setPageCount(searchResult.pageCount);
            } else {
                setPageCount(0);
            }

            if (objectHas(searchResult, 'pageNo')) {
                setPageNo(searchResult.pageNo);
            } else {
                setPageNo(0);
            }
        }

    }, [searchResult]);

    const handleCheckAllChange = (event) => {
        const value = event.target.checked;
        dispatch(sessionAnnotationsRowCheckAllSet(value));
    };

    const handleCheckChange = (event, rowId) => {
        dispatch(sessionAnnotationsRowCheckSet(event.target.checked, rowId));
    };

    const handleDeleteRows = () => {

        if (arrayExists(annotations)) {

            const queryObj = {
                annotatorId,
                docId,
                pageNo: 1,
                filter: searchResult.filter,
                queryType: searchResult.queryType
            };

            const deleteIds = [];
            annotations.forEach((row) => {
                if (row.selected === true) {
                    deleteIds.push(row._id);
                }
            });

            if (arrayExists(deleteIds)) {
                dispatch(sessionAnnotationsDeleteRequest(deleteIds, queryObj));
            }
        }
    };

    const handlePageNoChange = (event, value) => {

        const queryObj = {
            annotatorId,
            docId,
            searchTerm: annotatorId,
            pageNo: value,
            queryType: searchResult.queryType
        };

        dispatch(sessionAnnotationsPageQueryRequest(queryObj));
    };

    return (

        <SessionAnnotationsForm
            annotations={annotations}
            pageNo={pageNo}
            docType={docType}
            queryCount={queryCount}
            metadata={metadata}
            pageCount={pageCount}
            pageTitle={pageTitle}
            handleCheckAllChange={handleCheckAllChange}
            handleCheckChange={handleCheckChange}
            handleDeleteRows={handleDeleteRows}
            handlePageNoChange={handlePageNoChange}
        />
    )
}

export default SessionAnnotationsContainer;